/**
 * FL-246 Phase 8: ドライバー向けスケジュール登録ページ
 *
 * ドライバー・陸送員が自分の週間稼働スケジュールを登録・編集するページ
 * - 週間カレンダーUIでスケジュール入力
 * - 稼働日・稼働時間の一括登録
 * - 既存スケジュールの編集・削除（未承認のみ）
 *
 * Phase 8.1 実装：基本UI・フォーム構造
 * 責務：
 * - 権限チェック（DRIVER/LAND_TRANSPORTER のみアクセス可能）
 * - 週間日付範囲の計算
 * - WeeklyScheduleFormコンポーネントの統合
 * - スケジュール一括登録処理（Phase 8.2 で実装予定）
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useDateContext } from '@/hooks/useDate';
import { useToast } from '@/hooks/useToast';
import { DailyScheduleInput } from '@/types/schedule';
import { Role } from '@/types';
import { PageTitle } from '@/components/ui';
import Toast, { ToastContainer } from '@/components/ui/Toast';
import { WeeklyScheduleForm } from '@/components/schedule/WeeklyScheduleForm';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import logger from '@/utils/logger';

/**
 * 日付ユーティリティ: 週の開始日（月曜日）を取得
 */
const getWeekStart = (dateStr: string): Date => {
  const date = new Date(dateStr);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day; // 日曜日の場合は-6、それ以外は1-day
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() + diff);
  return weekStart;
};

/**
 * 日付ユーティリティ: 週の日付リストを生成（月〜日の7日間）
 */
const getWeekDates = (weekStart: Date): string[] => {
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
};

/**
 * ドライバー向けスケジュール登録ページ
 */
const ScheduleRegistrationPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedDate, goToPreviousDay, goToNextDay, goToToday } = useDateContext();
  const { toasts, success, removeToast } = useToast();

  // 週間スケジュールのstate
  const [weeklySchedules, setWeeklySchedules] = useState<DailyScheduleInput[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 週の開始日・終了日
  const [weekStart, setWeekStart] = useState<Date>(getWeekStart(selectedDate));
  const [weekDates, setWeekDates] = useState<string[]>(getWeekDates(weekStart));

  /**
   * selectedDate変更時に週の日付を再計算
   */
  useEffect(() => {
    const newWeekStart = getWeekStart(selectedDate);
    setWeekStart(newWeekStart);
    setWeekDates(getWeekDates(newWeekStart));
  }, [selectedDate]);

  /**
   * 週間スケジュールの初期化
   * 週の日付が変わったら、その週のスケジュールを読み込む
   */
  useEffect(() => {
    const initializeWeekSchedules = async () => {
      if (!user?.landTransporterId || !user?.companyId) {
        // ユーザー情報がない場合は空の初期値を設定
        const initialSchedules: DailyScheduleInput[] = weekDates.map(date => ({
          date,
          isWorking: false,
          timeStart: undefined,
          timeEnd: undefined,
          notes: undefined,
        }));
        setWeeklySchedules(initialSchedules);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Supabaseから既存スケジュール取得
        logger.info('initializeWeekSchedules: Fetching schedules', {
          landTransporterId: user.landTransporterId,
          companyId: user.companyId,
          weekDates,
        });

        const { getMySchedules } = await import('@/services/driverScheduleService');
        const existingSchedules = await getMySchedules(user.landTransporterId, user.companyId);

        logger.info('initializeWeekSchedules: Fetched schedules', {
          count: existingSchedules.length,
          schedules: existingSchedules,
        });

        // 既存スケジュールをマップ化（日付 → スケジュール）
        const scheduleMap = new Map(existingSchedules.map(s => [s.scheduleDate, s]));
        logger.info('initializeWeekSchedules: Schedule map created', {
          mapSize: scheduleMap.size,
          mapKeys: Array.from(scheduleMap.keys()),
          weekDates,
        });

        // 週間スケジュールを生成（既存データがあれば使用、なければ空）
        const weeklySchedules: DailyScheduleInput[] = weekDates.map(date => {
          const existing = scheduleMap.get(date);
          if (existing) {
            logger.info('initializeWeekSchedules: Found existing schedule for date', {
              date,
              schedule: existing,
            });
            return {
              date,
              isWorking: true,
              timeStart: existing.timeStart,
              timeEnd: existing.timeEnd,
              notes: existing.notes || undefined,
            };
          } else {
            logger.info('initializeWeekSchedules: No schedule found for date', { date });
            return {
              date,
              isWorking: false,
              timeStart: undefined,
              timeEnd: undefined,
              notes: undefined,
            };
          }
        });

        logger.info('initializeWeekSchedules: Generated weekly schedules', {
          count: weeklySchedules.length,
          workingDays: weeklySchedules.filter(s => s.isWorking).length,
          schedules: weeklySchedules,
        });

        setWeeklySchedules(weeklySchedules);
      } catch (err) {
        logger.error('initializeWeekSchedules: Failed to load schedules', err);
        setError('スケジュールの読み込みに失敗しました');

        // エラー時は空の初期値を設定
        const initialSchedules: DailyScheduleInput[] = weekDates.map(date => ({
          date,
          isWorking: false,
          timeStart: undefined,
          timeEnd: undefined,
          notes: undefined,
        }));
        setWeeklySchedules(initialSchedules);
      } finally {
        setIsLoading(false);
      }
    };

    initializeWeekSchedules();
  }, [weekDates, user?.landTransporterId, user?.companyId]);

  /**
   * スケジュール一括登録ハンドラー
   * Phase 8.2: createBulkDriverSchedules を使用して週間スケジュール一括登録
   */
  const handleScheduleSubmit = useCallback(async () => {
    if (!user?.id || !user?.companyId) {
      logger.warn('ScheduleRegistrationPage: user or companyId not found');
      setError('ユーザー情報が取得できませんでした');
      return;
    }

    // 陸送員IDの確認（user.id ではなく user.landTransporterId を使用）
    if (!user?.landTransporterId) {
      logger.warn('ScheduleRegistrationPage: landTransporterId not found in user object', {
        userId: user.id,
      });
      setError('陸送員情報が取得できませんでした。ページを再読み込みしてください。');
      return;
    }

    // UUID形式検証（RFC 4122準拠、version 4-5のみ：v1は MAC アドレス漏洩、v3 は MD5 衝突リスク）
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[45][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!UUID_REGEX.test(user.landTransporterId)) {
      logger.error('ScheduleRegistrationPage: landTransporterId is not valid UUID format', {
        userId: user.id,
        landTransporterId: user.landTransporterId,
      });
      setError('陸送員情報の形式が正しくありません。管理者にお問い合わせください。');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // 統合メッセージ用のカウント変数
      let deletedCount = 0;
      let createdCount = 0;

      // Phase 10: チェックが外された日付（isWorking: false）のスケジュール削除
      // 既存スケジュールを取得して、削除対象を特定
      logger.info('handleScheduleSubmit: Phase 10 - Deleting unchecked schedules');
      const { getMySchedules, deleteDriverSchedule, createBulkDriverSchedules } = await import(
        '@/services/driverScheduleService'
      );
      const existingSchedules = await getMySchedules(user.landTransporterId, user.companyId);

      // チェックが外された日付を特定
      const uncheckedDates = weeklySchedules
        .filter(s => !s.isWorking) // isWorking: false（チェックが外された）
        .map(s => s.date);

      logger.info('handleScheduleSubmit: Unchecked dates identified', {
        uncheckedDates,
        count: uncheckedDates.length,
      });

      // 削除対象スケジュールを特定（チェックが外されており、かつ既存スケジュールが存在）
      // べき等性確保: 既存スケジュールとの照合により、削除対象を正確に特定
      // ユーザーが再送信した場合でも、既に削除済みのスケジュールはリスト外となり、重複削除エラーを防止
      const schedulesToDelete = existingSchedules.filter(existing =>
        uncheckedDates.includes(existing.scheduleDate)
      );

      logger.info('handleScheduleSubmit: Schedules to delete', {
        count: schedulesToDelete.length,
        schedules: schedulesToDelete,
      });

      // 削除処理を実行（並列削除でパフォーマンス向上）
      if (schedulesToDelete.length > 0) {
        logger.info('handleScheduleSubmit: Starting deletion process', {
          count: schedulesToDelete.length,
        });

        // Promise.allSettled()で並列削除し、エラーハンドリング
        const deleteResults = await Promise.allSettled(
          schedulesToDelete.map(schedule =>
            deleteDriverSchedule(schedule.id)
              .then(() => {
                logger.debug('handleScheduleSubmit: Schedule deleted', {
                  scheduleId: schedule.id,
                  scheduleDate: schedule.scheduleDate,
                });
              })
              .catch(deleteErr => {
                logger.debug('handleScheduleSubmit: Failed to delete schedule', deleteErr, {
                  scheduleId: schedule.id,
                  scheduleDate: schedule.scheduleDate,
                });
                throw deleteErr;
              })
          )
        );

        // 失敗した削除を集計
        const failedCount = deleteResults.filter(r => r.status === 'rejected').length;

        if (failedCount > 0) {
          // TODO: Phase 10.1 でリトライUI追加予定
          logger.warn('handleScheduleSubmit: Partial deletion failure', {
            failedCount,
            totalCount: schedulesToDelete.length,
          });
          setError(`${failedCount}件のスケジュール削除に失敗しました。再度お試しください。`);
          setIsLoading(false);
          return;
        }

        // 削除完了サマリーログ
        logger.info('handleScheduleSubmit: Deletion summary', {
          totalDeleted: schedulesToDelete.length,
          message: `${schedulesToDelete.length}件のスケジュールを削除しました`,
        });

        // 削除件数を記録（統合メッセージに含める）
        deletedCount = schedulesToDelete.length;
      }

      // 稼働日のみフィルタリング
      const workingDays = weeklySchedules.filter(s => s.isWorking);

      if (workingDays.length === 0) {
        // 全日チェック解除の場合は削除のみで終了
        if (schedulesToDelete.length > 0) {
          // 削除後のスケジュール再取得
          try {
            const refreshedSchedules = await getMySchedules(user.landTransporterId, user.companyId);
            logger.info('handleScheduleSubmit: Fetched schedules after deletion', {
              count: refreshedSchedules.length,
              landTransporterId: user.landTransporterId,
            });

            // 既存スケジュールをマップ化
            const scheduleMap = new Map(refreshedSchedules.map(s => [s.scheduleDate, s]));

            // 週間スケジュールを更新
            const updatedSchedules: DailyScheduleInput[] = weekDates.map(date => {
              const existing = scheduleMap.get(date);
              if (existing) {
                return {
                  date,
                  isWorking: true,
                  timeStart: existing.timeStart,
                  timeEnd: existing.timeEnd,
                  notes: existing.notes || undefined,
                };
              } else {
                return {
                  date,
                  isWorking: false,
                  timeStart: undefined,
                  timeEnd: undefined,
                  notes: undefined,
                };
              }
            });

            setWeeklySchedules(updatedSchedules);
            // TODO: Phase 8.3でToast通知に置き換え予定
            setError(null); // エラー状態をクリア
            logger.info('handleScheduleSubmit: Deletion completed successfully', {
              count: schedulesToDelete.length,
              message: `${schedulesToDelete.length}件のスケジュールを削除しました`,
            });
          } catch (refreshErr) {
            logger.error(
              'handleScheduleSubmit: Failed to refresh schedules after deletion',
              refreshErr
            );
            setError('スケジュール削除後の更新に失敗しました。再度お試しください。');
            setIsLoading(false);
            return;
          }

          setIsLoading(false);
          return;
        }
        setError('稼働日が選択されていません。少なくとも1日は稼働日を選択してください。');
        setIsLoading(false);
        return;
      }

      logger.info(
        `ScheduleRegistrationPage: Submitting ${workingDays.length} working days`,
        workingDays
      );

      // Phase 8.2: createBulkDriverSchedules を呼び出し
      // ⚠️ user.id（認証ID）ではなく user.landTransporterId（陸送員マスターID）を使用
      const created = await createBulkDriverSchedules(
        user.landTransporterId,
        user.companyId,
        workingDays
      );

      logger.info('ScheduleRegistrationPage: Schedule registration success', {
        count: created.length,
      });

      // 登録件数を記録（統合メッセージに含める）
      createdCount = created.length;

      // スケジュール再取得（登録後は最新データを表示）
      logger.info('handleScheduleSubmit: Re-fetching schedules after registration', {
        landTransporterId: user.landTransporterId,
        companyId: user.companyId,
        weekDates,
      });
      const updatedSchedules = await getMySchedules(user.landTransporterId, user.companyId);
      logger.info('handleScheduleSubmit: Fetched updated schedules', {
        count: updatedSchedules.length,
        schedules: updatedSchedules,
      });

      // 既存スケジュールをマップ化
      const scheduleMap = new Map(updatedSchedules.map(s => [s.scheduleDate, s]));
      logger.info('handleScheduleSubmit: Schedule map created', {
        mapSize: scheduleMap.size,
        mapKeys: Array.from(scheduleMap.keys()),
        weekDates,
      });

      // 週間スケジュールを更新
      const refreshedSchedules: DailyScheduleInput[] = weekDates.map(date => {
        const existing = scheduleMap.get(date);
        if (existing) {
          logger.info('handleScheduleSubmit: Found existing schedule for date', {
            date,
            schedule: existing,
          });
          return {
            date,
            isWorking: true,
            timeStart: existing.timeStart,
            timeEnd: existing.timeEnd,
            notes: existing.notes || undefined,
          };
        } else {
          logger.info('handleScheduleSubmit: No schedule found for date', { date });
          return {
            date,
            isWorking: false,
            timeStart: undefined,
            timeEnd: undefined,
            notes: undefined,
          };
        }
      });

      logger.info('handleScheduleSubmit: Generated refreshed weekly schedules', {
        count: refreshedSchedules.length,
        workingDays: refreshedSchedules.filter(s => s.isWorking).length,
        schedules: refreshedSchedules,
      });

      setWeeklySchedules(refreshedSchedules);

      // 統合メッセージを表示
      const messages: string[] = [];
      if (deletedCount > 0) {
        messages.push(`削除 ${deletedCount}件`);
      }
      if (createdCount > 0) {
        messages.push(`登録 ${createdCount}件`);
      }
      if (messages.length > 0) {
        success(`${messages.join('、')}しました`, {
          duration: 3000,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'スケジュール登録に失敗しました';
      logger.error('ScheduleRegistrationPage: Failed to register schedules', err);

      // エラーメッセージの明確化
      if (message.includes('duplicate key')) {
        setError('既に登録されているスケジュールがあります。編集画面から変更してください。');
      } else if (message.includes('violates foreign key constraint')) {
        setError('陸送員情報が正しく登録されていません。管理者にお問い合わせください。');
      } else if (
        message.includes('violates check constraint') ||
        message.includes('invalid input syntax for type uuid')
      ) {
        setError('スケジュールデータの形式が正しくありません。管理者にお問い合わせください。');
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, weeklySchedules, weekDates]);

  /**
   * 週間スケジュール変更ハンドラー
   */
  const handleScheduleChange = useCallback((updatedSchedules: DailyScheduleInput[]) => {
    setWeeklySchedules(updatedSchedules);
  }, []);

  /**
   * 権限チェック（DRIVER または LAND_TRANSPORTER のみ表示）
   */
  const canRegisterSchedule =
    !!user && (user.role === Role.DRIVER || user.role === Role.LAND_TRANSPORTER);

  if (!canRegisterSchedule) {
    return (
      <div className='p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg'>
        <h2 className='text-lg font-semibold text-yellow-900 dark:text-yellow-200'>
          アクセス権限がありません
        </h2>
        <p className='text-sm text-yellow-700 dark:text-yellow-300 mt-2'>
          このページはドライバー・陸送員のみアクセス可能です。
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-4 p-4'>
      {/* Toast通知コンテナ */}
      <ToastContainer position='top-center'>
        {toasts.map(toast => (
          <Toast key={toast.id} {...toast} onDismiss={removeToast} />
        ))}
      </ToastContainer>

      {/* ページタイトル */}
      <PageTitle title='マイスケジュール登録' subtitle='週間稼働スケジュールを登録・編集' />

      {/* 日付ナビゲーション */}
      <div className='flex items-center justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg'>
        <button
          onClick={goToPreviousDay}
          className='p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors'
          title='前日'
        >
          <ChevronLeftIcon className='h-5 w-5 text-gray-600 dark:text-gray-400' />
        </button>

        <div className='flex flex-col items-center'>
          <span className='text-sm text-gray-600 dark:text-gray-400'>対象週</span>
          <span className='text-lg font-semibold text-gray-900 dark:text-white'>
            {weekDates[0]} 〜 {weekDates[6]}
          </span>
        </div>

        <button
          onClick={goToNextDay}
          className='p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors'
          title='翌日'
        >
          <ChevronRightIcon className='h-5 w-5 text-gray-600 dark:text-gray-400' />
        </button>

        <button
          onClick={goToToday}
          className='px-4 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors'
        >
          今週
        </button>
      </div>

      {/* エラーメッセージ表示 */}
      {error && (
        <div className='p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg'>
          <h3 className='text-sm font-semibold text-red-900 dark:text-red-200'>
            エラーが発生しました
          </h3>
          <p className='text-sm text-red-700 dark:text-red-300 mt-2'>{error}</p>
          <button
            onClick={() => setError(null)}
            className='mt-3 px-3 py-1 bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-200 rounded text-sm hover:bg-red-300 dark:hover:bg-red-700'
          >
            閉じる
          </button>
        </div>
      )}

      {/* 週間スケジュールフォーム */}
      <div className='p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
          週間スケジュール
        </h3>
        <WeeklyScheduleForm
          schedules={weeklySchedules}
          onScheduleChange={handleScheduleChange}
          disabled={isLoading}
        />
      </div>

      {/* 登録ボタン */}
      <div className='flex justify-end gap-3'>
        <button
          onClick={handleScheduleSubmit}
          disabled={isLoading}
          className='px-6 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {isLoading ? '登録中...' : '一括登録'}
        </button>
      </div>
    </div>
  );
};

export default ScheduleRegistrationPage;
